<template>
  <div class="admin-container">
    <div v-if="!isLoggedIn" class="login-container">
      <div class="login-box">
        <img :src="LogoImg" alt="Logo" class="admin-logo">
        <h2>后台管理系统</h2>
        <div class="login-form">
          <div class="form-group">
            <label for="username">用户名</label>
            <input type="text" id="username" v-model="username" placeholder="请输入用户名" @keyup.enter="login">
          </div>
          <div class="form-group">
            <label for="password">密码</label>
            <input type="password" id="password" v-model="password" placeholder="请输入密码" :disabled="!!challenge" @keyup.enter="login">
          </div>
          <TotpSetup v-if="challenge?.requiresTotpSetup" :secret="challenge.totpSecret" :uri="challenge.otpauthUri" />
          <div v-if="challenge" class="form-group"><label for="totp">Google 验证码／恢复码</label><input id="totp" v-model="verificationCode" autocomplete="one-time-code" placeholder="6 位验证码" @keyup.enter="verifyTotp"></div>
          <div v-if="loginError" class="error-message">{{ loginError }}</div>
          <button class="login-btn" @click="challenge ? verifyTotp() : login()" :disabled="isLoading">
            {{ isLoading ? '处理中...' : (challenge ? '验证并登入' : '继续') }}
          </button>
        </div>
      </div>
    </div>

    <div v-else class="admin-dashboard">
      <div class="admin-sidebar">
        <div class="sidebar-header">
          <img :src="LogoImg" alt="Logo" class="sidebar-logo">
          <h3>管理面板</h3>
        </div>
        <nav class="sidebar-nav">
          <div v-for="(item, index) in visibleNavItems" :key="index" class="nav-item"
            :class="{ active: activeNav === item.id }" @click="handleNavChange(item.id)">
            <span class="nav-icon">{{ item.icon }}</span>
            <span class="nav-text">{{ item.name }}</span>
          </div>
        </nav>
        <div class="sidebar-footer">
          <button class="logout-btn" @click="logout">退出登录</button>
        </div>
      </div>

      <div class="admin-content">
        <div class="content-header">
          <h2>{{ currentNavTitle }}</h2>
        </div>

        <!-- 博客管理模块 -->
        <div v-if="activeNav === 'blog'" class="content-body">
          <BlogManager ref="blogManagerRef" />
        </div>

        <!-- 图片管理模块 -->
        <div v-if="activeNav === 'image'" class="content-body">
          <ImageManager ref="imageManagerRef" />
        </div>

        <!-- 数据上传模块 -->
        <div v-if="activeNav === 'upload'" class="content-body">
          <UploadManager ref="uploadManagerRef" />
        </div>
        <div v-if="activeNav === 'security'" class="content-body"><AdminSecurityCenter /></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, inject, onMounted, watch, nextTick } from 'vue'
import LogoImg from '@/assets/img/logo.png'
import BlogManager from '@/components/BlogManager.vue'
import ImageManager from '@/components/ImageManager.vue'
import UploadManager from '@/components/UploadManager.vue'
import AdminSecurityCenter from '@/components/AdminSecurityCenter.vue'
import TotpSetup from '@/components/TotpSetup.vue'
import { adminSecurity, beginAdminLogin, checkAdminAuth, configureAdminSecurity, finishAdminLogin, hasAdminPermission, logoutAdmin } from '@/adminSecurity'

// 获取 API 基础 URL
const apiBaseUrl = inject('apiBaseUrl')

// 登录状态
const isLoggedIn = ref(false)
const isLoading = ref(false)
const username = ref('')
const password = ref('')
const verificationCode = ref('')
const loginError = ref('')
const challenge = computed(() => adminSecurity.challenge)

// 导航状态
const activeNav = ref('blog')
const navItems = [
  { id: 'blog', name: '博客管理', icon: '📝' },
  { id: 'image', name: '图片管理', icon: '🖼️' },
  { id: 'upload', name: '数据上传', icon: '📤' },
  { id: 'security', name: '登入与安全', icon: '🔐' }
]
const navPermissions = { blog: 'settings.edit', image: 'images.upload', upload: 'data.import' }
const visibleNavItems = computed(() => navItems.filter(item => item.id === 'security' || !navPermissions[item.id] || hasAdminPermission(navPermissions[item.id])))
watch(visibleNavItems, items => { if (!items.some(item => item.id === activeNav.value)) activeNav.value = items[0]?.id || 'security' }, { immediate: true })

// 博客管理组件引用
const blogManagerRef = ref(null)
// 图片管理组件引用
const imageManagerRef = ref(null)
// 数据上传组件引用
const uploadManagerRef = ref(null)

// 计算当前导航标题
const currentNavTitle = computed(() => {
  const currentNav = navItems.find(item => item.id === activeNav.value)
  return currentNav ? currentNav.name : ''
})

// 监听导航变化，当切换回博客管理时重新加载数据
const handleNavChange = (navId) => {
  activeNav.value = navId
}

// 监听 activeNav 的变化
watch(activeNav, async (newValue) => {
  if (newValue === 'blog') {
    // 使用 nextTick 确保组件已经渲染完成
    await nextTick()
    if (blogManagerRef.value) {
      blogManagerRef.value.loadBlogs()
    }
  } else if (newValue === 'image') {
    // 使用 nextTick 确保组件已经渲染完成
    await nextTick()
    if (imageManagerRef.value) {
      imageManagerRef.value.loadImages()
    }
  } else if (newValue === 'upload') {
    // 使用 nextTick 确保组件已经渲染完成
    await nextTick()
    if (uploadManagerRef.value && uploadManagerRef.value.checkStatus) {
      uploadManagerRef.value.checkStatus()
    }
  }
})

// 检查是否已登录
const checkLoginStatus = async () => {
  try {
    const data = await checkAdminAuth()
    isLoggedIn.value = !!data.authenticated
    if (isLoggedIn.value) {
    // 登录成功后加载博客列表
    setTimeout(() => {
      if (blogManagerRef.value) {
        blogManagerRef.value.loadBlogs()
      }
    }, 100) }
  } catch (_) { isLoggedIn.value = false }
}

// 登录方法
const login = async () => {
  if (!username.value || !password.value) {
    loginError.value = '请输入用户名和密码'
    return
  }

  isLoading.value = true
  loginError.value = ''

  try {
    const data = await beginAdminLogin(username.value, password.value)
    if (!data.success) loginError.value = data.message || '用户名或密码错误'
  } catch (error) {
    loginError.value = '登录失败，请稍后再试'
    console.error('登录错误:', error)
  } finally {
    isLoading.value = false
  }
}

const verifyTotp = async () => { if (!verificationCode.value) { loginError.value = '请输入 Google 验证码'; return } isLoading.value = true; loginError.value = ''; try { const data = await finishAdminLogin(verificationCode.value); if (data.authenticated) { isLoggedIn.value = true; verificationCode.value = ''; if (adminSecurity.recoveryCodes.length) alert(`请立即保存恢复码：\n${adminSecurity.recoveryCodes.join('\n')}`) } else loginError.value = data.message || '验证码错误' } catch (error) { loginError.value = error.message || '验证失败' } finally { isLoading.value = false } }

// 退出登录
const logout = async () => {
  await logoutAdmin()
  isLoggedIn.value = false
}

// 组件挂载时检查登录状态
onMounted(() => {
  configureAdminSecurity(apiBaseUrl)
  checkLoginStatus()
})
</script>

<style>
/* 全局样式，不使用 scoped */
.html-editor.dark-mode .ql-editor {
  background-color: #1e1e1e !important;
  /* color: #e0e0e0 !important; */
}

.html-editor.dark-mode .ql-toolbar {
  background-color: #2d2d2d !important;
  border-color: #444 !important;
}

.html-editor.dark-mode .ql-stroke {
  stroke: #e0e0e0 !important;
}

.html-editor.dark-mode .ql-fill {
  fill: #e0e0e0 !important;
}

.html-editor.dark-mode .ql-picker {
  color: #e0e0e0 !important;
}

.html-editor.dark-mode .ql-picker-options {
  background-color: #2d2d2d !important;
  border-color: #444 !important;
}

.html-editor.dark-mode .ql-tooltip {
  background-color: #2d2d2d !important;
  color: #e0e0e0 !important;
  border-color: #444 !important;
}

.html-editor.dark-mode .ql-picker-item {
  color: #e0e0e0 !important;
}

/* 添加暗色模式下的表格样式 */
.data-table {
  color: #333;
}

.data-table th {
  background-color: #f8f8f8;
  color: #333;
}

.data-table tr:hover {
  background-color: #f9f9f9;
}

.blog-content {
  color: #666;
}

/* 表单备注样式 */
.form-note {
  display: block;
  margin-top: 5px;
  color: #414141;
  font-size: 12px;
}
</style>

<style scoped>
.admin-container {
  min-height: 100vh;
  background-color: #f5f5f5;
  font-family: 'Arial', sans-serif;
}

/* 登录页样式 */
.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a1a 0%, #333 100%);
}

.login-box {
  width: 400px;
  padding: 30px;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 10px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  text-align: center;
}

.admin-logo {
  width: 120px;
  margin-bottom: 20px;
}

.login-form {
  margin-top: 30px;
}

.form-group {
  margin-bottom: 20px;
  text-align: left;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
  color: #333;
}

.form-group input {
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 16px;
  transition: border-color 0.3s;
}

.form-group input:focus {
  border-color: #FFD700;
  outline: none;
  box-shadow: 0 0 5px rgba(255, 215, 0, 0.3);
}

.error-message {
  color: #ff3333;
  margin-bottom: 15px;
  font-size: 14px;
}

.login-btn {
  width: 100%;
  padding: 12px;
  background-color: #FFD700;
  color: #333;
  border: none;
  border-radius: 5px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s;
}

.login-btn:hover {
  background-color: #f0c800;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.login-btn:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* 管理面板样式 */
.admin-dashboard {
  display: flex;
  min-height: 100vh;
}

.admin-sidebar {
  width: 250px;
  background-color: #1a1a1a;
  color: #fff;
  display: flex;
  flex-direction: column;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
}

.sidebar-header {
  padding: 20px;
  text-align: center;
  border-bottom: 1px solid #333;
}

.sidebar-logo {
  width: 80px;
  margin-bottom: 10px;
}

.sidebar-nav {
  flex: 1;
  padding: 20px 0;
}

.nav-item {
  padding: 15px 20px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s;
}

.nav-item:hover {
  background-color: #333;
}

.nav-item.active {
  background-color: #FFD700;
  color: #1a1a1a;
  font-weight: bold;
}

.nav-icon {
  margin-right: 15px;
  font-size: 20px;
}

.sidebar-footer {
  padding: 20px;
  border-top: 1px solid #333;
}

.logout-btn {
  width: 100%;
  padding: 10px;
  background-color: transparent;
  color: #fff;
  border: 1px solid #fff;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s;
}

.logout-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.admin-content {
  flex: 1;
  background-color: #f5f5f5;
  overflow-y: auto;
}

.content-header {
  padding: 20px;
  background-color: #fff;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.content-body {
  padding: 20px;
}

.module-container {
  background-color: #fff;
  border-radius: 5px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 20px;
}

.action-bar {
  display: flex;
  justify-content: space-between;
  margin: 20px 0;
}

.action-btn {
  padding: 10px 20px;
  background-color: #FFD700;
  color: #333;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s;
}

.action-btn:hover {
  background-color: #f0c800;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.search-input,
.filter-select {
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  width: 250px;
}

.empty-state {
  padding: 50px 0;
  text-align: center;
  color: #999;
  font-style: italic;
}

/* 图片库样式 */
.image-gallery {
  margin-top: 20px;
  min-height: 300px;
  border: 1px dashed #ddd;
  border-radius: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* 上传区域样式 */
.upload-area {
  margin-top: 20px;
}

.upload-box {
  border: 2px dashed #ddd;
  border-radius: 5px;
  padding: 40px;
  text-align: center;
  margin-bottom: 20px;
  transition: all 0.3s;
}

.upload-box:hover {
  border-color: #FFD700;
  background-color: rgba(255, 215, 0, 0.05);
}

.upload-icon {
  font-size: 40px;
  margin-bottom: 15px;
  display: block;
}

.upload-btn {
  margin-top: 15px;
  padding: 10px 20px;
  background-color: #FFD700;
  color: #333;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s;
}

.upload-btn:hover {
  background-color: #f0c800;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.upload-list {
  margin-top: 20px;
  min-height: 200px;
  border: 1px solid #eee;
  border-radius: 5px;
  padding: 15px;
}

/* 响应式样式 */
@media (max-width: 768px) {
  .admin-dashboard {
    flex-direction: column;
  }

  .admin-sidebar {
    width: 100%;
    height: auto;
  }

  .sidebar-nav {
    display: flex;
    flex-wrap: wrap;
    padding: 10px;
  }

  .nav-item {
    flex: 1;
    min-width: 120px;
    text-align: center;
    justify-content: center;
  }

  .nav-icon {
    margin-right: 5px;
  }

  .login-box {
    width: 90%;
    max-width: 400px;
  }
}

@media (max-width: 480px) {
  .action-bar {
    flex-direction: column;
    gap: 10px;
  }

  .search-input,
  .filter-select {
    width: 100%;
  }
}

/* 博客管理样式 */
.blog-manager {
  width: 100%;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
  background-color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border-radius: 5px;
  overflow: hidden;
}

.data-table th,
.data-table td {
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.data-table th {
  background-color: #f8f8f8;
  font-weight: bold;
  color: #333;
}

.data-table tr:hover {
  background-color: #f9f9f9;
}

.blog-title {
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.status-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: bold;
}

.status-published {
  background-color: #e6f7e6;
  color: #2e7d32;
}

.status-draft {
  background-color: #fff8e1;
  color: #ff8f00;
}

.action-cell {
  white-space: nowrap;
}

.table-btn {
  padding: 5px 10px;
  margin-right: 5px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.edit-btn {
  background-color: #e3f2fd;
  color: #1976d2;
}

.edit-btn:hover {
  background-color: #bbdefb;
}

.delete-btn {
  background-color: #ffebee;
  color: #d32f2f;
}

.delete-btn:hover {
  background-color: #ffcdd2;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
}

.page-btn {
  padding: 6px 12px;
  background-color: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  background-color: #e0e0e0;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  margin: 0 15px;
  font-size: 14px;
  color: #666;
}

.search-box {
  position: relative;
}

.search-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 50px 0;
  color: #999;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 15px;
  opacity: 0.5;
}

/* 博客表单样式 */
.blog-form {
  background-color: white;
  border-radius: 5px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.blog-form h4 {
  margin-top: 0;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}

.form-row {
  display: flex;
  gap: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group.half {
  flex: 1;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
  color: #333;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.3s;
  background-color: #fff;
  color: #333;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  border-color: #FFD700;
  outline: none;
  box-shadow: 0 0 5px rgba(255, 215, 0, 0.3);
}

.checkbox-group {
  display: flex;
  align-items: center;
}

.checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.checkbox-label input {
  margin-right: 8px;
  width: auto;
}

.image-upload-container {
  border: 1px dashed #ddd;
  border-radius: 4px;
  padding: 15px;
  text-align: center;
  transition: all 0.3s;
}

.image-upload-container:hover {
  border-color: #FFD700;
  background-color: rgba(255, 215, 0, 0.05);
}

.image-preview {
  position: relative;
  display: inline-block;
}

.image-preview img {
  max-width: 100%;
  max-height: 200px;
  border-radius: 4px;
}

.remove-image-btn {
  position: absolute;
  top: -10px;
  right: -10px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #ff5252;
  color: white;
  border: none;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

.upload-btn-wrapper {
  position: relative;
  overflow: hidden;
  display: inline-block;
}

.upload-btn-wrapper input[type=file] {
  position: absolute;
  left: 0;
  top: 0;
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.cancel-btn {
  padding: 10px 20px;
  background-color: #f5f5f5;
  color: #333;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
}

.cancel-btn:hover {
  background-color: #e0e0e0;
}

.submit-btn {
  padding: 10px 20px;
  background-color: #FFD700;
  color: #333;
  border: none;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s;
}

.submit-btn:hover {
  background-color: #f0c800;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

/* 模态框样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-container {
  background-color: white;
  border-radius: 5px;
  padding: 20px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.modal-container h4 {
  margin-top: 0;
  color: #333;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

/* 加载指示器样式 */
.loading-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 50px 0;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 215, 0, 0.3);
  border-radius: 50%;
  border-top-color: #FFD700;
  animation: spin 1s ease-in-out infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.success-message {
  background-color: rgba(0, 128, 0, 0.1);
  color: #2e7d32;
  padding: 10px 15px;
  border-radius: 5px;
  margin-bottom: 15px;
  border-left: 4px solid #2e7d32;
}

.error-message {
  background-color: rgba(255, 0, 0, 0.1);
  color: #d32f2f;
  padding: 10px 15px;
  border-radius: 5px;
  margin-bottom: 15px;
  border-left: 4px solid #d32f2f;
}

.blog-content {
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: monospace;
  font-size: 12px;
  color: #666;
}

.code-editor {
  font-family: monospace;
  font-size: 14px;
  line-height: 1.5;
  tab-size: 2;
  background-color: #f8f8f8;
}

/* 添加 Quill 编辑器样式 */
:deep(.ql-editor) {
  min-height: 300px;
  background-color: #fff;
  font-size: 14px;
  line-height: 1.6;
}

:deep(.ql-toolbar) {
  background-color: #f8f8f8;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  border-color: #ddd;
}

.editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.editor-actions {
  display: flex;
  gap: 10px;
}

.toggle-html-button,
.mode-toggle-button {
  background-color: #e3f2fd;
  color: #1976d2;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.toggle-html-button:hover,
.mode-toggle-button:hover {
  background-color: #bbdefb;
}

.mode-toggle-button {
  background-color: #f0f0f0;
  color: #333;
}

.mode-toggle-button.active {
  background-color: #333;
  color: #fff;
}

.mode-toggle-button:hover {
  background-color: #e0e0e0;
}

.mode-toggle-button.active:hover {
  background-color: #444;
}

.html-editor {
  height: 400px;
  margin-bottom: 20px;
  border-radius: 4px;
  overflow: hidden;
}

.code-editor.dark-mode {
  background-color: #1e1e1e;
  color: #e0e0e0;
  border-color: #444;
}

.view-btn {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.view-btn:hover {
  background-color: #c8e6c9;
}
</style>
